import type { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import type { ComponentProps } from 'react'

import { FinancialCockpitPendingActionOwner } from '../../../../api/types'
import { usePendingActionInvocation } from '../../../cockpitPendingActions/hooks/usePendingActionInvocation'
import { buildSobEntryAction, useMissingSobCategories } from '../attentionStatusSobActuals'

import { AttentionNextStepRow } from './AttentionNextStepRow'

type TRowProps = Omit<ComponentProps<typeof AttentionNextStepRow>, 'onClick'>

interface IManagerSobEntryProps {
  row: TRowProps
  offering: string
  /** Where the step goes when the partner's payload names no category owing actuals. */
  onFallback: () => void
}

/**
 * A PBM's `Submit SOB` on the manager cockpit: the SOB dialog of the partner's own cockpit, opened
 * on the first category still owing its actuals. Rendered inside the partner's scope, so the
 * dialog reads and writes that partner, as the status' detail does; the next category opens on
 * the next press, once the cockpit has read the answer back.
 */
export const ManagerSobEntry = ({ row, offering, onFallback }: IManagerSobEntryProps) => {
  const { categories } = useMissingSobCategories(offering as ProgramTypes)
  const { selectAction, isActionBusy, modals } = usePendingActionInvocation(
    offering as ProgramTypes
  )

  const first = categories[0]
  const action = first
    ? (first.action ??
      buildSobEntryAction(first.category, FinancialCockpitPendingActionOwner.Mine, 0))
    : null

  return (
    <>
      <AttentionNextStepRow
        {...row}
        disabled={Boolean(action?.ActionBlockedBy) || (action ? isActionBusy(action) : false)}
        onClick={() => (action ? selectAction(action) : onFallback())}
      />
      {modals}
    </>
  )
}

ManagerSobEntry.displayName = 'ManagerSobEntry'
